import clone from 'lodash.clonedeep';

/**
 * Bioland Global Route Middleware
 * 
 * This middleware runs on EVERY route navigation (both SSR and client-side).
 * It is responsible for:
 * 1. Ensuring site context (siteCode, locale, config) is available
 * 2. Validating and redirecting locale prefixes
 * 3. Fetching page data and menus
 * 4. Initializing Pinia stores with fetched data
 * 
 * EXECUTION ORDER:
 * - Nuxt plugins run first (site.js should initialize siteStore)
 * - Then route middleware runs (this file)
 * - Then page components render
 * 
 * PROBLEM THIS SOLVES:
 * On the very first SSR request, there's no context cookie yet.
 * The site.js plugin SHOULD initialize siteStore, but due to timing issues
 * or plugin failures, the store may be empty when this middleware runs.
 * 
 * SOLUTION:
 * ensureSiteContext() acts as a safety net - if siteStore is empty,
 * it fetches context directly from the API before proceeding.
 * This guarantees context is ALWAYS available before page rendering.
 */
export default defineNuxtRouteMiddleware(async (to, from) => {
  const nuxtApp     = useNuxtApp();
  const path        = to.path;

  // Check for component viewer routes early - before any other processing
  const isComponentRoute = path.match(/^\/[a-z]{2}\/components\//);
  
  // Components that need store data (menus, site context) to render properly
  const componentsNeedingStores = [
    'widget-content-types-stats',
  ];
  
  // Check if this component route needs store data
  const componentName = isComponentRoute ? path.split('/components/')[1]?.split('?')[0] : null;
  const needsStoreData = componentName && componentsNeedingStores.includes(componentName);
  
  if (isComponentRoute && !needsStoreData) {
    return; // Exit middleware entirely for component routes that don't need stores
  }

  await changeLocale();

  const siteStore   = useSiteStore(nuxtApp.$pinia);
  const pStore      = usePageStore(nuxtApp.$pinia);
  const menuStore   = useMenusStore(nuxtApp.$pinia);
  const meStore     = useMeStore(nuxtApp.$pinia);

  const requestCookieHeader = useRequestHeaders(['cookie']);
  const clientCookie        = useCookie(hasSessionCookieClient())

  // Path is NOT stored in cookie - it's passed directly to API calls
  // Context cookie only stores: siteCode, locale, defaultLocale, locales

  /**
   * CRITICAL: Ensure site context is available before ANY other operations.
   * 
   * Why this exists:
   * - The site.js plugin should initialize siteStore before middleware runs
   * - However, on first SSR hit (no cookie), timing issues can cause the store to be empty
   * - Without context, we can't fetch pages, menus, or render anything meaningful
   * 
   * What it does:
   * - Checks if siteStore already has data (happy path - plugin worked)
   * - If empty, fetches context from /api/context/{siteCode}/{locale}
   * - Initializes siteStore with the fetched data
   * - Sets the context cookie for subsequent requests
   * 
   * This guarantees that siteStore.siteCode, siteStore.host, etc. are ALWAYS
   * available when components render, preventing "undefined" in URLs.
   */
  await ensureSiteContext();

  // Return the redirect: carrying on after it re-ran this middleware for a site locale missing
  // from i18n (tz /sw/search) until the head ran out of heap.
  const localeRedirect = isValidLocalePrefix();

  if(localeRedirect) return localeRedirect;
  
  /**
   * Final safety check after ensureSiteContext.
   * 
   * If we STILL don't have siteCode after the fallback fetch, something is
   * seriously wrong (network error, invalid hostname, DMSM down, etc.)
   * 
   * Client-side: Reload the app to re-trigger the full initialization flow
   * Server-side: Log error and return early (page will render empty, but won't crash)
   */
  if(!siteStore.siteCode) {
    if(import.meta.client) return reloadNuxtApp();
    console.error('[middleware] Failed to initialize site context');
    return;
  }
  
  await getMe();

  // Use siteStore.locale which is guaranteed to be set by the site plugin
  const locale = nuxtApp.$i18n?.locale?.value || siteStore.locale || siteStore.defaultLocale;
  const   getPage          = useGetPage(locale);
  
  // For component routes that need stores, only fetch menus (no page data)
  if (isComponentRoute && needsStoreData) {
    const { data: menuData } = await getMenus() || { data: undefined };
    if (menuData?.value) {
      menuStore.loadAllMenus(menuData.value);
    }
    return; // Skip page fetch for component routes
  }
  
  const [ pData, fetch ]   = await Promise.all([getPage(path), getMenus()]);
  const { data: menuData } = fetch || { data: undefined};

  if(!pData) throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true });
  if(pData?.redirect) {
    await abortNavigation();
    
    return navigateTo({ path: pData.redirect, query: to.query  }, { redirectCode: 301 });
  }

  pStore.initialize(pData);

  stashDocumentCacheTtl();

  if(menuData?.value)
    menuStore.loadAllMenus(menuData.value);

  refreshMenusIfDocumentStale();


  /**
   * Work out whether this document may carry an age-tiered Cache-Control, and stash the TTL for
   * `server/plugins/document-cache-ttl.ts` to apply (BL-1059).
   *
   * Only the DECISION happens here, because only here is the Drupal node in hand.
   * `server/middleware/cache-control.js` runs before the node is fetched, so the flat 15s it stamps
   * is the best it can do. The header WRITE deliberately happens later, in a `beforeResponse` hook:
   * that is the last point where the final status code and the final header are both known, so it
   * cannot hand a long TTL to an error page or quietly weaken a `no-store` set after this runs.
   *
   * The gate itself lives in `shouldTierDocument` as a pure function, so each rule is unit-testable.
   * Two of its inputs are worth spelling out here:
   *
   * - The session cookie, NOT `meStore.isAuthenticated`. `server/middleware/auth.js` computes that
   *   flag as `!isContentManager && isAuthenticated`, so it is FALSE for content managers, site
   *   managers and administrators - exactly the users whose HTML carries edit affordances, their
   *   email and a CSRF token in the SSR payload. Gating on it would have shipped those responses to
   *   a shared CDN. The flag is still consulted as a second layer.
   * - `to.path`, not `siteStore.isHomePage`. The getter reaches for `useRoute()` inside a Pinia
   *   getter after several awaits, and matches on the store's locale rather than the URL's - so a
   *   `/fr` home request while the store held `en` would miss the cap entirely.
   *
   * The home page is capped at the youngest tier: its own node is usually years old, but it renders
   * live widgets (latest news, discussions, GBIF) whose data is fetched during SSR and baked into
   * the payload. Every other system page (search variants, news, credits, forums, sitemaps) is not
   * tiered at all: they are shells whose body is other content, so their `changed` date is
   * meaningless as a staleness signal.
   *
   * @returns {void}
   */
  function stashDocumentCacheTtl(){
    if(!import.meta.server) return;

    const event = nuxtApp.ssrContext?.event;

    if(!event) return;

    // Stamped on every server render, tiered or not, so the client can tell how old the HTML it
    // hydrated from is (see refreshMenusIfDocumentStale).
    nuxtApp.payload.renderedAt = Date.now();

    const locales = useRuntimeConfig().public.locales?.map(({ code })=> code) || [];
    const isHome  = to.path === '/' || to.path === siteStore.homePath || locales.some((code)=> to.path === `/${code}`);

    const permitted = shouldTierDocument({
      isServer      : true,
      hasSession    : hasDrupalSessionCookie(event.node?.req?.headers?.cookie) || meStore.isAuthenticated,
      isBypass      : 'seachain-taisce' in (to.query || {}),
      isContentPage : pStore.isPage || pStore.isMediaPage,
      // Home may be a system_pages term or a node, but it is the one shell worth tiering: it is
      // the highest-traffic page and the 5-minute cap below already bounds its staleness.
      isSystemPage  : pStore.isSystemPage && !isHome,
    });

    if(!permitted) return;

    const tiered = resolveDocumentCacheTtl(pStore.page?.changed);

    if(tiered === null) return;

    event.context.documentCacheTtl = isHome ? Math.min(tiered, CACHE_TTL.FIVE_MINUTES) : tiered;
  }

  /**
   * On hydration, replace the menus baked into a stale tiered document with fresh ones.
   *
   * The document may have sat at the CDN for up to `DOCUMENT_MAX_TTL`; the menus API is cached
   * for `CACHE_TTL.MENUS`. When the HTML is older than that, one request to `/api/menus` brings the
   * navigation back within its own TTL without giving up the long document TTL. The request is
   * answered from the Nitro `menus-index` cache (keyed by Host and locale, not by page), so it costs
   * an origin round-trip but not a Drupal fan-out. Fire-and-forget: navigation never waits on it,
   * and a failure leaves the server-rendered menus in place.
   *
   * Runs once per page load: `isHydrating` stays true until suspense resolves, and a redirecting
   * middleware can re-enter this one before then.
   *
   * @returns {void}
   */
  function refreshMenusIfDocumentStale(){
    if(!import.meta.client || !nuxtApp.isHydrating || nuxtApp._staleMenusRefreshed) return;
    if(!documentIsStaleFor(nuxtApp.payload?.renderedAt, CACHE_TTL.MENUS)) return;

    nuxtApp._staleMenusRefreshed = true;

    $fetch(`/api/menus`, { query: clone(siteStore.params) })
      .then((menus)=> { if(menus) menuStore.loadAllMenus(menus); })
      .catch((e)=> console.debug('[02.bioland] stale-menu refresh failed', e?.statusCode || e?.message));
  }

  /**
   * Validates that the URL has a proper locale prefix (e.g., /en/, /fr/).
   * If the locale prefix is missing or invalid, redirects to the default locale.
   * Also removes duplicate locale prefixes to prevent redirect loops.
   * 
   * @returns {void|NavigateToOptions} Redirect if invalid, undefined if valid
   */
  function isValidLocalePrefix(){
    const { locales} = useRuntimeConfig().public;
    const   preFixes = locales.map(({ code })=> code);

    // siteStore.defaultLocale is guaranteed to be set by site.js plugin
    // which runs before this middleware
    const defaultLocale = siteStore.defaultLocale;
    const pathSegments = to.path.split('/').filter(Boolean); // Remove empty strings
    const pathLocale = pathSegments[0];
    
    if(!defaultLocale) {
      // This should never happen - plugin ensures defaultLocale is set
      console.error('defaultLocale not set in siteStore - plugin may have failed');
      return;
    }
    
    // Handle root path: / should redirect to /${defaultLocale}
    if(!pathLocale || pathSegments.length === 0) {
      return navigateTo(`/${defaultLocale}`);
    }
    
    const isValid = preFixes.includes(pathLocale);

    // Check if there are duplicate locale prefixes (e.g., /en/en/page or /en/fr/page)
    if(isValid && pathSegments.length > 1 && preFixes.includes(pathSegments[1])) {
      // Remove all consecutive locale prefixes, keep only the first valid one
      let cleanSegments = [pathSegments[0]];
      let i = 1;
      // Skip any additional locale codes
      while(i < pathSegments.length && preFixes.includes(pathSegments[i])) {
        i++;
      }
      // Add remaining path segments (if any exist after removing duplicate locales)
      if(i < pathSegments.length) {
        cleanSegments = cleanSegments.concat(pathSegments.slice(i));
      }
      // If only locale prefixes existed, cleanSegments will just be [locale]
      const cleanPath = '/' + cleanSegments.join('/');
      return navigateTo(cleanPath.replace(/\/{2,}/g, '/'), { redirectCode: 301, replace: true });
    }

    if(!isValid) {
      // Invalid or missing locale prefix: replace with default locale
      const pathWithoutFirstSegment = pathSegments.slice(1).join('/');
      const correctedPath = pathWithoutFirstSegment ? `/${defaultLocale}/${pathWithoutFirstSegment}` : `/${defaultLocale}`;
      // Normalize multiple consecutive slashes to single slash
      return navigateTo(correctedPath.replace(/\/{2,}/g, '/'), { redirectCode: 301, replace: true });
    }
}

  /**
   * Detects if the user is navigating to a different locale.
   * Compares the current i18n locale with the locale in the URL path.
   * 
   * @returns {string|false} The new locale code if changing, false if not
   */
  function isLocaleChange(){
    if(!nuxtApp.$i18n) return false;
    
    const { locale } = nuxtApp.$i18n;

    if(to.path.startsWith(`/${locale.value}`)) return false;

    const pathLocale = to.path.split('/')[1];

    // A code i18n does not know is left for isValidLocalePrefix to redirect.
    return useRuntimeConfig().public.locales.some(({ code }) => code === pathLocale) && pathLocale;
  }

  /**
   * Updates the i18n locale if the URL indicates a locale change.
   * Waits for the locale change to complete before continuing.
   * 
   * @returns {Promise<void>}
   */
  async function changeLocale(){
    const isChange = isLocaleChange();

    if(!isChange) return;

    if(nuxtApp.$i18n) {
      nuxtApp.$i18n.setLocale(isChange)
      await nuxtApp.$i18n.waitForPendingLocaleChange()
    }
  }
  
  /**
   * Fetches all menu data if not already loaded.
   * Menus are cached in menuStore.isLoaded to avoid refetching on every navigation.
   * 
   * @returns {Promise<undefined|FetchResult>} Menu fetch result, or undefined if already loaded
   */
  async function getMenus(){
    if(menuStore.isLoaded) return undefined;

    return useFetch(`/api/menus`, { query: clone({ ...siteStore.params, path:to.path })});
  }

  /**
   * Fetches the current user's authentication state and roles.
   * Uses Drupal session cookies (SSESS*) to authenticate.
   * 
   * @returns {Promise<void>}
   * @throws {Error} If the /api/me request fails
   */
  async function getMe() {
    try {
      const headers = getAuthCookieHeaders();
      const { data, error } = await useFetch(`/api/me`, {
        method: 'GET',
        headers,
        query: clone({ ...siteStore.params, path: to.path })
      });

      if (!error.value && data.value) meStore.initialize(data);
    } catch (e) {
      console.error(e);
      throw createError({ statusCode: 500, statusMessage: 'Internal Server Error', message: 'Error getting user' });
    }
  }

  /**
   * Builds the authentication cookie headers for Drupal API requests.
   * 
   * Priority order:
   * 1. SSESS cookie from incoming request headers (SSR with authenticated user)
   * 2. localDrupalSession from env (localhost dev fallback for first request)
   * 3. Client-side cookie (browser navigations)
   * 
   * The localhost-dev middleware sets a cookie from localDrupalSession,
   * but on the FIRST request that cookie won't be in requestCookieHeader yet.
   * So we still need the env fallback for that first request.
   * 
   * @returns {{ cookie: string|object }} Headers object with cookie
   */
  function getAuthCookieHeaders() {
    // 1. Check if request already has SSESS cookie (normal authenticated flow)
    if (requestCookieHeader?.cookie?.includes('SSESS')) {
      return requestCookieHeader;
    }

    // 2. Localhost dev fallback: use env var for first request (server-side only)
    const { public: { isLocalHost } } = useRuntimeConfig();
    const localDrupalSession = import.meta.server ? useRuntimeConfig().localDrupalSession : null;
    
    if (isLocalHost && localDrupalSession) {
      return { cookie: localDrupalSession };
    }

    // 3. Client-side: use cookie from browser
    return { cookie: { [hasSessionCookieClient()]: clientCookie.value } };
  }

  /**
   * Ensures site context is available in siteStore.
   * 
   * PURPOSE:
   * This is the middleware's safety net for context initialization.
   * The site.js plugin should normally handle this, but on first SSR requests
   * (no cookie) or if the plugin fails, siteStore may be empty.
   * 
   * WHY IT'S NEEDED:
   * - Components like hero-image.vue use siteStore.host to build image URLs
   * - If siteStore.host is undefined, URLs become "https://undefined.undefined/..."
   * - This breaks hero images, page titles, and any component using site context
   * 
   * HOW IT WORKS:
   * 1. Check if siteStore already has required fields (siteCode, locale, defaultLocale)
   * 2. If yes, return immediately (plugin worked correctly)
   * 3. If no, derive siteCode from hostname (e.g., "be.localhost" → "be")
   * 4. Fetch context from /api/context/{siteCode}/{locale}
   * 5. Initialize siteStore with the response
   * 6. Set context cookie so subsequent requests have context
   * 
   * @returns {Promise<void>}
   */
  async function ensureSiteContext(){
    // If siteStore already has context (from site plugin), we're done
    if(siteStore.siteCode && siteStore.locale && siteStore.defaultLocale) return;

    const runtime = useRuntimeConfig().public;

    // Derive site identifier from hostname (be.localhost -> be)
    const requestUrl = useRequestURL();
    const hostName = requestUrl?.hostname;
    const siteIdentifier = getSiteIdentifierFromHost(hostName);
    
    if(!siteIdentifier) {
      console.error('[middleware] Cannot derive siteCode from hostname:', hostName);
      return;
    }

    // Determine locale from URL path, fallback to 'und' (undefined) to let server use default
    const runtimeLocales = (runtime?.locales || []).map(({ code }) => code);
    const pathLocale = to.path.split('/')[1];
    const requestedLocale = runtimeLocales.includes(pathLocale) ? pathLocale : 'und';

    try {
      // Fetch context from server API (uses cached DMSM config)
      const data = await $fetch(`/api/context/${encodeURIComponent(siteIdentifier)}/${encodeURIComponent(requestedLocale)}`);

      // Initialize siteStore with fetched data + runtime config
      siteStore.initialize({ ...data, ...{
        locale: data?.locale,
        identifier: data?.siteCode,
        siteCode: data?.siteCode,
        defaultLocale: data?.defaultLocale,
        config: data?.config,
        siteName: data?.siteName,
        gaiaApi: runtime?.gaiaApi,
        multiSiteCode: runtime?.multiSiteCode,
        baseHost: runtime?.baseHost,
        env: runtime?.env,
        homePath: data?.homePath
      }});

      // Set context cookie for subsequent requests (SSR response will include Set-Cookie)
      const contextCookie = useCookie('context');
      if(!contextCookie.value && data?.siteCode) {
        contextCookie.value = {
          siteCode: data.siteCode,
          locale: data.locale,
          defaultLocale: data.defaultLocale,
          locales: data.locales
        };
      }
    } catch(e) {
      console.error('[middleware] Failed to fetch context:', e);
    }
  }

  function isKnownDevHost(host, baseHost) {
    if(!host) return false;

    return host === 'localhost'
      || host === '127.0.0.1'
      || host.endsWith('.localhost')
      || (baseHost ? host.endsWith(`.${baseHost}`) : false);
  }

  /**
   * Extracts the site identifier from a hostname.
   * 
   * In the multi-site architecture, the site code is the first subdomain:
   * - "be.localhost" → "be" (Belgium site in dev)
   * - "be.chm-cbd.net" → "be" (Belgium site in production)
   * - "seed.chm-cbd.net" → "seed" (Seed/template site)
   * 
   * @param {string} hostName - The hostname to parse
   * @returns {string|null} The site identifier, or null if hostname is invalid
   */
  function getSiteIdentifierFromHost(hostName){
    if(!hostName) return null;

    const { baseHost } = useRuntimeConfig().public;
    if(!isKnownDevHost(hostName, baseHost)) return null;

    const parts = hostName.split('.');
    return parts.length > 1 ? parts[0] : null;
  }
})